import React from 'react';
import Footer from '../components/Footer';
import MarketingPublicNav from '../components/MarketingPublicNav';
import { PublicBandSection, PublicMarketingLayout } from '../components/PublicMarketingLayout';

const Compliance: React.FC = () => {
  return (
    <PublicMarketingLayout>
      <MarketingPublicNav />

      <PublicBandSection band={1} className="px-6 pb-8 pt-16">
        <div className="mx-auto max-w-4xl">
          <h1 className="mb-4 bg-gradient-to-r from-violet-500 to-blue-500 bg-clip-text text-5xl font-bold text-transparent">
            Compliance & Regulatory
          </h1>
          <p className="text-gray-400">Last updated: March 28, 2025</p>
        </div>
      </PublicBandSection>

      <PublicBandSection band={2} className="px-6 pb-16 pt-4">
        <div className="mx-auto max-w-4xl">
        <div className="space-y-12">
          <section>
            <h2 className="text-2xl font-bold mb-4 text-white">Our Commitment to Compliance</h2>
            <p className="text-gray-400 mb-4 leading-relaxed">
              kol.my is committed to maintaining the highest standards of regulatory compliance and ethical business practices. We operate in accordance with all applicable laws and regulations in Malaysia and across Southeast Asia.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-white">Advertising Standards</h2>
            <div className="bg-white/5 border border-white/10 p-6 rounded-xl space-y-4">
              <div>
                <h3 className="text-xl font-bold mb-2 text-white">Malaysian Advertising Standards</h3>
                <p className="text-gray-400 leading-relaxed">
                  All campaigns on kol.my must comply with the Malaysian Communication and Multimedia Content Guidelines and the Malaysian Advertising Standards Authority (MASA) codes.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-bold mb-2 text-white">Disclosure Requirements</h3>
                <p className="text-gray-400 leading-relaxed">
                  KOLs must clearly disclose sponsored content using #ad, #sponsored, or #partner as required by the Malaysian Consumer Protection Act and international advertising standards.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-bold mb-2 text-white">Prohibited Content</h3>
                <p className="text-gray-400 leading-relaxed">
                  We strictly prohibit campaigns promoting illegal products, deceptive practices, discrimination, or content that violates public morality.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-white">Data Protection Compliance</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-violet-500/10 border border-violet-500/20 p-6 rounded-xl">
                <h3 className="text-xl font-bold mb-3 text-white">PDPA (Malaysia)</h3>
                <p className="text-gray-400 leading-relaxed">
                  We comply fully with the Personal Data Protection Act 2010, ensuring proper collection, processing, and protection of personal data.
                </p>
              </div>
              <div className="bg-blue-500/10 border border-blue-500/20 p-6 rounded-xl">
                <h3 className="text-xl font-bold mb-3 text-white">GDPR (EU Users)</h3>
                <p className="text-gray-400 leading-relaxed">
                  For users in the European Union, we comply with the General Data Protection Regulation, providing comprehensive data rights.
                </p>
              </div>
              <div className="bg-emerald-500/10 border border-emerald-500/20 p-6 rounded-xl">
                <h3 className="text-xl font-bold mb-3 text-white">ISO 27001</h3>
                <p className="text-gray-400 leading-relaxed">
                  Our information security management system follows ISO 27001 standards for data security best practices.
                </p>
              </div>
              <div className="bg-amber-500/10 border border-amber-500/20 p-6 rounded-xl">
                <h3 className="text-xl font-bold mb-3 text-white">PCI DSS</h3>
                <p className="text-gray-400 leading-relaxed">
                  Payment card data is processed in compliance with Payment Card Industry Data Security Standards.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-white">Anti-Money Laundering (AML)</h2>
            <p className="text-gray-400 mb-4 leading-relaxed">
              kol.my implements robust AML controls in accordance with Malaysian Anti-Money Laundering Act 2001 (AMLA):
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-400">
              <li>Customer due diligence and KYC verification</li>
              <li>Transaction monitoring and suspicious activity reporting</li>
              <li>Enhanced due diligence for high-risk users</li>
              <li>Sanctions screening against international lists</li>
              <li>Staff training on AML awareness</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-white">Campaign Verification</h2>
            <div className="space-y-4">
              <div className="bg-white/5 border border-white/10 p-6 rounded-xl">
                <h4 className="font-bold mb-2 text-white">KOL Authenticity Checks</h4>
                <p className="text-gray-400 leading-relaxed">
                  We verify social media account ownership and audience authenticity to prevent fake followers and engagement.
                </p>
              </div>
              <div className="bg-white/5 border border-white/10 p-6 rounded-xl">
                <h4 className="font-bold mb-2 text-white">Brand Legitimacy Verification</h4>
                <p className="text-gray-400 leading-relaxed">
                  All brands undergo verification to ensure they are legitimate businesses before posting campaigns.
                </p>
              </div>
              <div className="bg-white/5 border border-white/10 p-6 rounded-xl">
                <h4 className="font-bold mb-2 text-white">Content Review Process</h4>
                <p className="text-gray-400 leading-relaxed">
                  Automated and manual content review ensures compliance with advertising standards before publication.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-white">Report a Compliance Issue</h2>
            <div className="bg-red-500/10 border-l-4 border-red-500 p-6 rounded-r-lg">
              <p className="text-red-200 mb-4 leading-relaxed">
                If you encounter any suspicious activity, fraud, or compliance violations on our platform, please report it immediately.
              </p>
              <p className="text-gray-400 leading-relaxed">
                <strong className="text-white">Compliance Hotline:</strong> +60 3-1234 5679<br />
                <strong className="text-white">Email:</strong> i@kol.my<br />
                <strong className="text-white">Anonymous Reporting:</strong> Use our secure form below
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-white">Certifications & Audit</h2>
            <p className="text-gray-400 mb-4 leading-relaxed">
              We undergo regular third-party audits to ensure compliance with industry standards:
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="text-center p-4 bg-white/5 border border-white/10 rounded-lg">
                <div className="text-3xl mb-2">Quarterly Penetration Testing</div>
              </div>
              <div className="text-center p-4 bg-white/5 border border-white/10 rounded-lg">
                <div className="text-3xl mb-2">PDPA Compliance Review</div>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-white">Contact Our Compliance Team</h2>
            <p className="text-gray-400 leading-relaxed">
              For compliance-related inquiries or to request our compliance documentation:<br />
              <strong className="text-white">Email:</strong> i@kol.my<br />
              <strong className="text-white">Business Hours:</strong> Monday-Friday, 9am-6pm MYT
            </p>
          </section>
        </div>
        </div>
      </PublicBandSection>

      <Footer />
    </PublicMarketingLayout>
  );
};

export default Compliance;
